import React, {
  useCallback, useMemo, useState, useRef,
} from 'react';
import type { ComponentProps as PropsOf, ReactNode as Node } from 'react';
import type {
  ModalBodyProps,
  ModalHeaderProps,
  ModalProps,
  SurfaceProps,
} from '@americanexpress/dls-react';
import {
  ModalBody,
  ModalHeader,
  Surface,
} from '@americanexpress/dls-react';
import { FadeIn } from '@americanexpress/web-react-extensions';
import cz from 'classnames';

import ShortSilentVideo from './ShortSilentVideo';
import Modal from '../dls-plus/Modal';
import { flex, noop } from '../../utils';
import useClickTracking from '../../hooks/FutureUHL/useClickTracking';
import styles from './ShortSilentVideoModal.scss';
import { DataAttributes, SurfaceVariant } from './types';

// url and autoPlay are props on this component already.
export type OtherVideoProps = Omit<PropsOf<typeof ShortSilentVideo>, 'url' | 'autoPlay'>;

export type ApplicableFadeInProps = Omit<PropsOf<typeof FadeIn>, 'children' | 'isIn'>;

type SpecificProps = {
  url?: string,
  id: string,
  label: string,
  children?: NonNullable<Node>,
  showContent?: boolean,
  headerContent?: Node,
  onClose?: () => unknown,
  autoPlay?: boolean,
  overlay?: Node,
  showOverlay?: boolean,
  containerProps?: Partial<SurfaceProps>,
  overlayFadeProps?: Partial<ApplicableFadeInProps>
  videoSized?: boolean | null,
  videoTimeoutSec?: number,
  videoProps?: Partial<OtherVideoProps>,
  contentFadeProps?: Partial<ApplicableFadeInProps>,
  videoFadeProps?: Partial<ApplicableFadeInProps>,
  headerProps?: Partial<ModalHeaderProps>,
  bodyProps?: Partial<ModalBodyProps>,
  surfaceVariant?: SurfaceVariant | null,
};

export type Props = SpecificProps
  & Omit<ModalProps, keyof SpecificProps | 'aria-labelledby'>
  & DataAttributes;

export const DEFAULT_TRANSITION_DURATION_SEC = 0.25;

const ShortSilentVideoModal = ({
  url,
  id,
  label,
  children = <span />,
  showContent = false,
  headerContent = <span />,
  onClose = noop,
  autoPlay = true,
  overlay = null,
  showOverlay = false,
  containerProps,
  overlayFadeProps,
  videoSized: videoSizedProp = null,
  videoTimeoutSec,
  contentFadeProps,
  videoFadeProps,
  videoProps,
  headerProps,
  bodyProps,
  ...modalProps
}: Props) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const trackClick = useClickTracking();
  const [isOpen, setIsOpen] = useState(true);
  const close = useCallback(() => {
    const sessionTimeoutModal = document.querySelector('#amexSessionExtend');
    const qualtricsModal = document.querySelector('.QSIWebResponsive');
    if (!sessionTimeoutModal
      && !qualtricsModal
    ) {
      setIsOpen(false);
      // tracking how the modal was dismissed
      // eslint-disable-next-line no-restricted-globals -- event is global
      if (event && event instanceof KeyboardEvent && event.key === 'Escape') {
        trackClick({ element: 'closed-by-escape' });
        // eslint-disable-next-line no-restricted-globals -- event is global
      } else if (event
        // eslint-disable-next-line no-restricted-globals -- event is global
        && event.target instanceof HTMLElement && !modalRef?.current?.contains(event.target)) {
        trackClick({ element: 'closed-by-click-outside-modal' });
      } else {
        trackClick({ element: 'closed-by-x' });
      }
      onClose();
    }
  }, [trackClick, onClose]);

  const {
    className: contentContainerClass,
    'data-testid': contentContainerTestId,
    ...othersForContentContainer
  } = contentFadeProps || {};

  const videoSized = videoSizedProp === null ? !showContent : videoSizedProp;

  const videoContainerProps = useMemo(() => ({
    className: cz(
      'position-absolute',
      'width-full',
      'height-full',
      flex('justify-center'),
      { [styles.videoSize]: videoSized }),
  }), [videoSized]);

  const { className: overlayClass, ...othersForOverlayContainer } = overlayFadeProps || {};
  const { className: headerClass, ...othersForHeader } = headerProps || {};
  const { className: bodyClass, ...othersForBody } = bodyProps || {};

  if (!isOpen) {
    return null;
  }

  const labelId = `${id}-label`;

  return (
    <Modal
      ref={modalRef}
      onClose={close}
      id={id}
      aria-labelledby={labelId}
      {...modalProps}
      innerContainerClassName={cz(
        modalProps.innerContainerClassName,
        styles.shortSilentVideoModal,
        { [styles.videoSize]: videoSized },
        { 'width-full': !videoSized })}
    >
      <Surface
        data-testid="short-silent-video-modal-children"
        variant="foreground"
        className="height-full"
        {...containerProps || {}}
      >
        <FadeIn
          data-testid="video-fade-in"
          isIn={!showOverlay && !showContent}
          durationSec={DEFAULT_TRANSITION_DURATION_SEC}
          {...videoFadeProps || {}}
        >
          <ShortSilentVideo
            autoPlay={autoPlay}
            forContainer={videoContainerProps}
            url={url}
            videoTimeoutSec={videoTimeoutSec}
            {...videoProps || {}}
          />
        </FadeIn>
        <ModalHeader
          className={cz(
            'pad-2-b',
            flex('align-items-center', 'justify-end'),
            styles.transparent,
            headerClass)}
          {...othersForHeader}
        >
          <span id={labelId} className="display-none">
            {label}
          </span>
          {headerContent}
        </ModalHeader>
        <FadeIn
          data-testid={contentContainerTestId || 'content-fade-in'}
          isIn={showContent}
          className={cz(contentContainerClass, 'width-full')}
          durationSec={DEFAULT_TRANSITION_DURATION_SEC}
          {...othersForContentContainer}
        >
          <ModalBody
            className={cz(
              'pad-0',
              videoSized ? 'position-absolute' : 'position-relative',
              { 'no-scroll': !showContent },
              bodyClass)}
            {...othersForBody}
          >
            {children}
          </ModalBody>
        </FadeIn>
        {/* TODO: add a ModalFooter? */}
        <FadeIn
          data-testid="overlay-fade-in"
          isIn={showOverlay}
          className={cz('position-absolute', styles.bottomLeft, overlayClass)}
          durationSec={DEFAULT_TRANSITION_DURATION_SEC}
          {...othersForOverlayContainer}
        >
          {overlay}
        </FadeIn>
      </Surface>
    </Modal>
  );
};

export default ShortSilentVideoModal;
